/**
 * Telegram WebApp helpers.
 * Falls back to a local mock user so the UI can be developed in a normal browser.
 */
export function getTelegramWebApp() {
  return window.Telegram?.WebApp || null
}

export function initTelegramUi() {
  const tg = getTelegramWebApp()
  if (!tg) return null
  try {
    tg.ready()
    tg.expand()
    if (typeof tg.setHeaderColor === 'function') tg.setHeaderColor('#0d4169')
    if (typeof tg.setBackgroundColor === 'function') tg.setBackgroundColor('#f8fafc')
  } catch {
    // ignore unsupported methods
  }
  return tg
}

export function getTelegramUser() {
  const tg = getTelegramWebApp()
  const user = tg?.initDataUnsafe?.user
  if (user?.id) {
    return {
      telegram_id: user.id,
      username: user.username || null,
      first_name: user.first_name || null,
      last_name: user.last_name || null,
      full_name: [user.first_name, user.last_name].filter(Boolean).join(' ') || null,
    }
  }

  // Browser / local development fallback
  // const mockKey = 'Ahun_jobs_dev_tg_user'
  // try {
  //   const saved = localStorage.getItem(mockKey)
  //   if (saved) return JSON.parse(saved)
  // } catch {
  //   // ignore
  // }

  // const mock = {
  //   telegram_id: 900001,
  //   username: 'dev_user',
  //   first_name: 'Dev',
  //   last_name: 'User',
  //   full_name: 'Dev User',
  // }
  // try {
  //   localStorage.setItem(mockKey, JSON.stringify(mock))
  // } catch {
  //   // ignore
  // }
  // return mock
}

export function getStartParam() {
  const tg = getTelegramWebApp()

  const fromUnsafe = tg?.initDataUnsafe?.start_param
  if (fromUnsafe) return String(fromUnsafe)

  if (tg?.initData) {
    try {
      const fromInitData = new URLSearchParams(tg.initData).get('start_param')
      if (fromInitData) return fromInitData
    } catch {
      // ignore malformed initData
    }
  }

  const params = new URLSearchParams(window.location.search)
  const fromQuery =
    params.get('startapp') ||
    params.get('tgWebAppStartParam') ||
    params.get('start_param')
  if (fromQuery) return fromQuery

  const hash = String(window.location.hash || '').replace(/^#/, '')
  if (hash) {
    const hashParams = new URLSearchParams(hash.startsWith('?') ? hash.slice(1) : hash)
    const fromHash = hashParams.get('startapp') || hashParams.get('tgWebAppStartParam')
    if (fromHash) return fromHash
  }

  return null
}

/** Telegram may populate start_param shortly after WebApp init — poll briefly. */
export function waitForStartParam(timeoutMs = 3000) {
  return new Promise((resolve) => {
    const started = Date.now()

    function tryRead() {
      const param = getStartParam()
      if (param) {
        resolve(param)
        return
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null)
        return
      }
      setTimeout(tryRead, 50)
    }

    tryRead()
  })
}

export function parseStartParam(param) {
  if (!param) return { type: 'home' }
  if (param === 'post_job') return { type: 'post_job' }
  if (param === 'my_jobs') return { type: 'my_jobs' }
  if (param === 'my_applications') return { type: 'my_applications' }
  if (param.startsWith('job_')) {
    const rest = param.slice('job_'.length)
    const parts = rest.split('_')
    return { type: 'job', id: Number(parts[0]) }
  }
  if (param.startsWith('application_')) {
    const id = Number(param.slice('application_'.length))
    if (Number.isFinite(id) && id > 0) return { type: 'application', id }
  }
  if (param.startsWith('applicants_')) {
    const rest = param.slice('applicants_'.length)
    const parts = rest.split('_')
    const id = Number(parts[0])
    const entryIndex = parts.length > 1 ? Number(parts[1]) : undefined
    return {
      type: 'applicants',
      id,
      entryIndex: parts.length > 1 && !Number.isNaN(entryIndex) ? entryIndex : undefined,
    }
  }
  return { type: 'home' }
}

/**
 * Navigate to the screen matching a Telegram startapp / start_param value.
 * Returns true when a deep-link route was applied.
 */
export function applyStartParamRoute(router, startParam) {
  const parsed = parseStartParam(startParam)

  if (parsed.type === 'post_job') {
    router.replace({ name: 'post_job' })
    return true
  }
  if (parsed.type === 'my_jobs') {
    router.replace({ name: 'my_jobs' })
    return true
  }
  if (parsed.type === 'my_applications') {
    router.replace({ name: 'my_applications' })
    return true
  }
  if (parsed.type === 'job' && parsed.id) {
    router.replace({ name: 'job_detail', params: { id: String(parsed.id) } })
    return true
  }
  if (parsed.type === 'application' && parsed.id) {
    router.replace({ name: 'application_detail', params: { id: String(parsed.id) } })
    return true
  }
  if (parsed.type === 'applicants' && parsed.id) {
    const query = {}
    if (parsed.entryIndex != null && !Number.isNaN(parsed.entryIndex)) {
      query.entry = String(parsed.entryIndex)
    }
    router.replace({ name: 'applicants', params: { id: String(parsed.id) }, query })
    return true
  }

  return false
}

export function hapticLight() {
  try {
    getTelegramWebApp()?.HapticFeedback?.impactOccurred?.('light')
  } catch {
    // ignore
  }
}
